/**
 * API client for user profile (GET /me, PATCH user).
 */

import { getApiBaseUrl, getApiVersion } from '../../../shared/api/runtimeConfig'
import { clearTokensIfUnauthorized, getAccessToken } from '../utils/tokenStorage'
import { sessionExpiredMessage } from '../utils/tokenStorage'
import i18n from '../../../i18n'
import type { FormatPreferences } from '../../../shared/format'

const API_URL = getApiBaseUrl()
const API_VERSION = getApiVersion()

export interface UserProfile {
  id: number
  username: string
  email: string
  name: string
  first_name: string
  last_name: string
  address: string
  city: string
  country: string
  phone: string
  /** 'M' / 'F' / '' (non précisé) — sert aux accords grammaticaux (ex. "aventurier·ère"). */
  gender: 'M' | 'F' | ''
  url?: string
  /** Vrai = photos envoyées avec leurs métadonnées (position, date, appareil). Faux par défaut. */
  keep_photo_metadata?: boolean
  /** Préférence « montrer les heures de mes sorties » proposée à chaque partage. */
  share_times_default?: boolean
  /** Faux pour un compte Google (pas de mot de passe). */
  has_password?: boolean
  /** Profil → Format : choix explicites seulement, voir shared/format/preferences.ts. */
  format_preferences?: FormatPreferences
  is_staff?: boolean
  is_superuser?: boolean
}

function getAuthHeaders(): HeadersInit {
  const token = getAccessToken()
  const headers: HeadersInit = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

function extractErrorMessage(err: unknown): string {
  if (!err || typeof err !== 'object') return i18n.t('auth:userApi.erreur_inconnue')
  const d = err as Record<string, unknown>
  if (typeof d.detail === 'string' && d.detail.trim()) return d.detail
  if (typeof d.message === 'string' && d.message.trim()) return d.message
  for (const v of Object.values(d)) {
    if (Array.isArray(v) && v.length && typeof v[0] === 'string') return (v as string[]).join(', ')
  }
  return i18n.t('auth:userApi.erreur_inconnue')
}

/**
 * Fetch current user profile.
 * En cas de 401 (token expiré/invalide), efface les tokens et lance une erreur avec sessionExpiredMessage().
 */
export async function getMe(options?: { signal?: AbortSignal }): Promise<UserProfile> {
  const url = `${API_URL}/${API_VERSION}/users/me/`
  const res = await fetch(url, { headers: getAuthHeaders(), signal: options?.signal })
  if (res.status === 401) {
    clearTokensIfUnauthorized()
    throw new Error(sessionExpiredMessage())
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(extractErrorMessage(body) || `HTTP ${res.status}`)
  }
  return res.json() as Promise<UserProfile>
}

/**
 * Update current user profile. Pass partial fields to patch.
 */
export async function updateMe(
  payload: Partial<Pick<UserProfile, 'first_name' | 'last_name' | 'city' | 'country' | 'phone' | 'address' | 'name' | 'gender' | 'keep_photo_metadata' | 'share_times_default' | 'format_preferences'>>,
  username: string,
  options?: { signal?: AbortSignal }
): Promise<UserProfile> {
  const url = `${API_URL}/${API_VERSION}/users/${encodeURIComponent(username)}/`
  const res = await fetch(url, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
    signal: options?.signal,
  })
  if (res.status === 401) {
    clearTokensIfUnauthorized()
    throw new Error(sessionExpiredMessage())
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(extractErrorMessage(body) || `HTTP ${res.status}`)
  }
  return res.json() as Promise<UserProfile>
}

/** Organization (minimal for managed-organization). */
export interface ManagedOrganization {
  id: number
  name: string
  description?: string
  managers: Array<{
    id: number
    user: { id: number; username: string; email?: string; name?: string; first_name?: string; last_name?: string }
    user_id: number
    role: string
    created_at: string
  }>
  created_at?: string
  updated_at?: string
}

/** Entrée légère de GET /users/managed-organizations/ */
export interface ManagedOrganizationListItem {
  id: number
  name: string
  slug?: string | null
  operator_id?: number | null
  organization_type_id?: number | null
  organization_type?: string | null
  logo_url?: string | null
  is_directly_managed?: boolean
}

export interface ManagedOrganizationsResponse {
  is_staff: boolean
  count: number
  organizations: ManagedOrganizationListItem[]
}

/**
 * Fetch the organization managed by the current user, if any.
 * Returns null when the user does not manage any organization (404).
 * @deprecated Prefer getManagedOrganizations() for multi-org support.
 */
export async function getManagedOrganization(
  options?: { signal?: AbortSignal }
): Promise<ManagedOrganization | null> {
  const url = `${API_URL}/${API_VERSION}/users/managed-organization/`
  const res = await fetch(url, { headers: getAuthHeaders(), signal: options?.signal })
  if (res.status === 401) {
    clearTokensIfUnauthorized()
    throw new Error(sessionExpiredMessage())
  }
  if (res.status === 404) return null
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(extractErrorMessage(body) || `HTTP ${res.status}`)
  }
  return res.json() as Promise<ManagedOrganization>
}

/**
 * Fetch all organizations managed by the current user (cascade + staff = all).
 */
export async function getManagedOrganizations(
  options?: { signal?: AbortSignal; q?: string }
): Promise<ManagedOrganizationsResponse> {
  const params = new URLSearchParams()
  if (options?.q?.trim()) params.set('q', options.q.trim())
  const qs = params.toString()
  const url = `${API_URL}/${API_VERSION}/users/managed-organizations/${qs ? `?${qs}` : ''}`
  const res = await fetch(url, { headers: getAuthHeaders(), signal: options?.signal })
  if (res.status === 401) {
    clearTokensIfUnauthorized()
    throw new Error(sessionExpiredMessage())
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(extractErrorMessage(body) || `HTTP ${res.status}`)
  }
  return res.json() as Promise<ManagedOrganizationsResponse>
}

/**
 * Supprime définitivement son compte (confirm = « SUPPRIMER », mot de passe si le compte en a un).
 * erase_public faux : le contenu public reste en ligne sans nom.
 */
export async function deleteMyAccount(payload: {
  confirm: string
  password?: string
  erase_public: boolean
}): Promise<void> {
  const res = await fetch(`${API_URL}/${API_VERSION}/users/me/delete/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  })
  if (res.status === 401) {
    clearTokensIfUnauthorized()
    throw new Error(sessionExpiredMessage())
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(extractErrorMessage(body) || `HTTP ${res.status}`)
  }
}

export type DataExportStatus = {
  status: 'pending' | 'running' | 'ready' | 'failed' | null
  created_at: string | null
  expires_at: string | null
  size_bytes: number | null
  next_allowed_at: string | null
  /** Lien signé valable 10 minutes, présent quand l’archive est prête. */
  download_url: string | null
}

async function dataExportRequest(method: 'GET' | 'POST', signal?: AbortSignal): Promise<DataExportStatus> {
  const res = await fetch(`${API_URL}/${API_VERSION}/users/me/export/`, {
    method,
    headers: getAuthHeaders(),
    signal,
  })
  if (res.status === 401) {
    clearTokensIfUnauthorized()
    throw new Error(sessionExpiredMessage())
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(extractErrorMessage(body) || `HTTP ${res.status}`)
  }
  return res.json() as Promise<DataExportStatus>
}

/** « Télécharger toutes mes données » : état de la dernière demande. */
export function getDataExport(options?: { signal?: AbortSignal }): Promise<DataExportStatus> {
  return dataExportRequest('GET', options?.signal)
}

/** Nouvelle demande (une par 24 h) ; l’archive est préparée en arrière-plan. */
export function requestDataExport(): Promise<DataExportStatus> {
  return dataExportRequest('POST')
}

/** « Déconnecter tous mes appareils » : ferme toutes les sessions du compte, y compris celle-ci. */
export async function logoutAllDevices(): Promise<void> {
  const res = await fetch(`${API_URL}/${API_VERSION}/auth/logout-all/`, { method: 'POST', headers: getAuthHeaders() })
  if (!res.ok && res.status !== 401) throw new Error(`HTTP ${res.status}`)
}
